import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useKitchenOrders } from '@/features/pos/hooks/useKitchenOrders';
import { useUpdateOrderStatus } from '@/features/pos/hooks/usePOSOrders';
import { useKitchenSettings } from '@/features/pos/hooks/useKitchenSettings';
import { 
  Clock, 
  CheckCircle, 
  ChefHat,
  AlertCircle,
  Loader2,
  Volume2,
  VolumeX
} from 'lucide-react';
import { format, differenceInMinutes } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from '@/components/ui/toast/useToast';

export const Route = createFileRoute('/apps/pos/kitchen/')({
  component: KitchenDisplayPage,
});

function KitchenDisplayPage() {
  const { data: orders = [], isLoading } = useKitchenOrders();
  const { data: kitchenSettings } = useKitchenSettings();
  const updateStatus = useUpdateOrderStatus();
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [lastOrderCount, setLastOrderCount] = useState(0);

  // Play sound when new order arrives
  useEffect(() => {
    if (orders.length > lastOrderCount && soundEnabled && lastOrderCount > 0) {
      // Play notification sound
      const audio = new Audio('/notification.mp3');
      audio.play().catch(() => {}); // Ignore if sound fails
      
      toast.info('Nouvelle commande', 'Une nouvelle commande est arrivée!');
    }
    setLastOrderCount(orders.length);
  }, [orders.length, soundEnabled, lastOrderCount]);

  const handleStatusChange = async (orderId: string, newStatus: 'confirmed' | 'preparing' | 'ready') => {
    try {
      await updateStatus.mutateAsync({
        order_id: orderId,
        status: newStatus,
      });
      
      const statusText = {
        confirmed: 'Confirmée',
        preparing: 'En préparation',
        ready: 'Prête',
      }[newStatus];
      
      toast.success('Statut mis à jour', statusText);
    } catch (error: any) {
      toast.error('Erreur', error.message);
    }
  };

  const getTimerColor = (createdAt: string) => {
    const minutes = differenceInMinutes(new Date(), new Date(createdAt));
    const warning = kitchenSettings?.warning_time || 10;
    const urgent = kitchenSettings?.urgent_time || 15;
    const critical = kitchenSettings?.critical_time || 20;
    
    if (minutes < warning) return 'text-green-600';
    if (minutes < urgent) return 'text-yellow-600';
    if (minutes < critical) return 'text-orange-600';
    return 'text-red-600';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 border-yellow-400 text-yellow-800';
      case 'confirmed':
        return 'bg-blue-100 border-blue-400 text-blue-800';
      case 'preparing':
        return 'bg-purple-100 border-purple-400 text-purple-800';
      case 'ready':
        return 'bg-green-100 border-green-400 text-green-800';
      default:
        return 'bg-gray-100 border-gray-400 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'NOUVELLE';
      case 'confirmed':
        return 'CONFIRMÉE';
      case 'preparing':
        return 'EN COURS';
      case 'ready':
        return 'PRÊTE';
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <Loader2 className="w-12 h-12 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <ChefHat className="w-10 h-10 text-emerald-400" />
            <div>
              <h1 className="text-3xl font-bold">Écran Cuisine</h1>
              <p className="text-gray-400">Commandes en temps réel</p>
            </div>
          </div>

          <div className="flex items-center gap-6">
            {/* Order Count */}
            <div className="text-center">
              <p className="text-4xl font-bold text-emerald-400">{orders.length}</p>
              <p className="text-sm text-gray-400">Commandes</p>
            </div>

            {/* Sound Toggle */}
            <Button
              variant="outline"
              size="lg"
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="gap-2"
            >
              {soundEnabled ? (
                <>
                  <Volume2 className="w-5 h-5" />
                  Son activé
                </>
              ) : (
                <>
                  <VolumeX className="w-5 h-5" />
                  Son coupé
                </>
              )}
            </Button>

            {/* Time */}
            <div className="text-right">
              <p className="text-2xl font-bold">
                {format(new Date(), 'HH:mm')}
              </p>
              <p className="text-sm text-gray-400">
                {format(new Date(), 'd MMM yyyy', { locale: fr })}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Orders Grid */}
      <div className="p-6">
        {orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <CheckCircle className="w-20 h-20 text-emerald-400 mb-4" />
            <p className="text-2xl text-gray-400">Aucune commande en attente</p>
            <p className="text-gray-500 mt-2">Toutes les commandes sont traitées!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {orders.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                onStatusChange={handleStatusChange}
                getTimerColor={getTimerColor}
                getStatusColor={getStatusColor}
                getStatusLabel={getStatusLabel}
                isUpdating={updateStatus.isPending}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function OrderCard({
  order,
  onStatusChange,
  getTimerColor,
  getStatusColor,
  getStatusLabel,
  isUpdating,
}: any) {
  const [elapsed, setElapsed] = useState(0);

  // Update timer every second
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(differenceInMinutes(new Date(), new Date(order.created_at)));
    }, 1000);
    return () => clearInterval(interval);
  }, [order.created_at]);

  return (
    <Card className={`border-4 ${getStatusColor(order.status)} bg-white`}>
      <div className="p-6 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">
              {order.table_id || order.order_type}
            </h3>
            <p className="text-sm text-gray-600">#{order.order_number}</p>
          </div>
          
          {/* Timer */}
          <div className={`text-right ${getTimerColor(order.created_at)}`}>
            <div className="flex items-center gap-1">
              <Clock className="w-5 h-5" />
              <span className="text-3xl font-bold">{elapsed}</span>
              <span className="text-lg">min</span>
            </div>
            <p className="text-xs text-gray-600">
              {format(new Date(order.created_at), 'HH:mm')}
            </p>
          </div>
        </div>

        {/* Status Badge */}
        <div className={`inline-block px-4 py-2 rounded-lg font-bold text-sm ${getStatusColor(order.status)}`}>
          {getStatusLabel(order.status)}
        </div>

        {/* Order Type Badge */}
        {order.order_type === 'takeaway' && (
          <div className="bg-yellow-100 border-2 border-yellow-500 px-3 py-1 rounded-lg text-center font-bold text-yellow-800">
            À EMPORTER
          </div>
        )}
        {order.order_type === 'delivery' && (
          <div className="bg-blue-100 border-2 border-blue-500 px-3 py-1 rounded-lg text-center font-bold text-blue-800">
            LIVRAISON
          </div>
        )}

        {/* Items */}
        <div className="space-y-2">
          {order.items?.map((item: any) => (
            <div key={item.id} className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-gray-900 text-white rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0">
                  {item.quantity}
                </div>
                <div className="flex-1">
                  <p className="font-bold text-gray-900 text-lg">
                    {item.product_name}
                  </p>
                  {item.notes && (
                    <div className="mt-1 bg-yellow-100 border-l-4 border-yellow-500 px-2 py-1">
                      <p className="text-xs font-bold text-yellow-900">
                        ⚠️ {item.notes}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Order Notes */}
        {order.notes && (
          <div className="bg-red-50 border-2 border-red-500 rounded-lg p-3">
            <p className="text-xs font-bold text-red-700 mb-1">⚠️ NOTE COMMANDE:</p>
            <p className="text-sm font-bold text-red-900">{order.notes}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-2 pt-2">
          {order.status === 'pending' && (
            <Button
              onClick={() => onStatusChange(order.id, 'confirmed')}
              disabled={isUpdating}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold"
            >
              <CheckCircle className="w-5 h-5 mr-2" />
              Confirmer
            </Button>
          )}

          {order.status === 'confirmed' && (
            <Button
              onClick={() => onStatusChange(order.id, 'preparing')}
              disabled={isUpdating}
              className="w-full h-12 bg-purple-600 hover:bg-purple-700 text-white font-bold"
            >
              <ChefHat className="w-5 h-5 mr-2" />
              Commencer
            </Button>
          )}

          {order.status === 'preparing' && (
            <Button
              onClick={() => onStatusChange(order.id, 'ready')}
              disabled={isUpdating}
              className="w-full h-12 bg-green-600 hover:bg-green-700 text-white font-bold"
            >
              <CheckCircle className="w-5 h-5 mr-2" />
              Prête!
            </Button>
          )}

          {order.status === 'ready' && (
            <Button
              onClick={() => onStatusChange(order.id, 'completed')}
              disabled={isUpdating}
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold animate-pulse"
            >
              <CheckCircle className="w-5 h-5 mr-2" />
              Servir / Livrer
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
